import * as React from 'react'
import { cva, type VariantProps } from 'class-variance-authority'
import { Slot } from 'radix-ui'

import { cn } from '../../lib/utils'

export type ButtonVariant = 'default' | 'destructive' | 'outline' | 'secondary' | 'emerald' | 'amber' | 'ghost' | 'link';

const buttonVariants = cva(
  'tw:inline-flex tw:items-center tw:justify-center tw:border-0 tw:shadow-xs tw:gap-2 tw:whitespace-nowrap tw:rounded-lg tw:text-sm tw:font-medium tw:transition-all tw:disabled:pointer-events-none tw:disabled:opacity-50 tw:[&_svg]:pointer-events-none tw:[&_svg:not([class*=size-])]:size-4 tw:shrink-0 tw:[&_svg]:shrink-0 tw:outline-none tw:focus-visible:border-ring tw:focus-visible:ring-ring/50 tw:focus-visible:ring-[3px] tw:aria-invalid:ring-destructive/20 tw:dark:aria-invalid:ring-destructive/40 tw:aria-invalid:border-destructive',
  {
    variants: {
      variant: {
        default: 'tw:bg-primary tw:text-primary-foreground tw:hover:bg-primary/90',
        light: 'tw:bg-primary/20  tw:hover:bg-primary/30',
        destructive:
          'tw:bg-destructive tw:text-white tw:hover:bg-destructive/90 tw:focus-visible:ring-destructive/20 tw:dark:focus-visible:ring-destructive/40 tw:dark:bg-destructive/60',
        outline:
          'tw:border tw:border-input tw:bg-background tw:hover:bg-secondary tw:hover:text-secondary-foreground tw:dark:bg-input/30 tw:dark:border-input tw:dark:hover:bg-input/50',
        secondary:
          'tw:bg-secondary tw:text-secondary-foreground tw:hover:bg-secondary/80',
        emerald:
          'tw:bg-emerald-600 tw:text-white tw:hover:bg-emerald-600/80',
        amber:
          'tw:bg-amber-600 tw:text-white tw:hover:bg-amber-600/80',
        'amber-outline':
          'tw:border tw:border-amber-600 tw:bg-transparent tw:hover:bg-amber-600/10',
        ghost:
          'tw:hover:bg-accent tw:hover:text-accent-foreground tw:dark:hover:bg-accent/50',
        link: 'tw:text-primary tw:underline-offset-4 tw:hover:underline tw:bg-transparent tw:shadow-none tw:!px-0',
      },
      size: {
        default: 'tw:h-9 tw:px-4 tw:py-2 tw:has-[>svg]:px-3',
        xs: 'tw:h-6 tw:gap-1 tw:rounded-lg tw:px-3 tw:text-xs tw:has-[>svg]:pl-1.5 tw:[&_svg:not([class*=size-])]:size-3',
        sm: 'tw:h-8 tw:px-3 tw:rounded-lg tw:gap-1.5',
        lg: 'tw:h-10 tw:rounded-lg tw:px-6 tw:has-[>svg]:px-4',
        icon: 'tw:size-9',
        'icon-xs': 'tw:size-6 tw:rounded-lg tw:[&_svg:not([class*=size-])]:size-3',
        'icon-sm': 'tw:size-8',
        'icon-lg': 'tw:size-10',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  }
)

const Button = React.forwardRef<
  HTMLButtonElement,
  React.ComponentProps<'button'> &
    VariantProps<typeof buttonVariants> & {
      asChild?: boolean
    }
>(({ className, variant = 'default', size = 'default', asChild = false, ...props }, ref) => {
  const Comp = asChild ? Slot.Root : 'button'

  return (
    <Comp
      ref={ref}
      data-slot='button'
      data-variant={variant}
      data-size={size}
      className={cn(buttonVariants({ variant, size, className }))}
      {...props}
    />
  )
})
Button.displayName = 'Button'

export { Button, buttonVariants }
